import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useAuth } from '../../../contexts/AuthContext';
import { retryPublication } from '../../../services/adminApi';

interface RetryPublicationButtonProps {
  publicationId: number;
  onRetryAccepted?: () => void;
}

const RetryPublicationButton: React.FC<RetryPublicationButtonProps> = ({
  publicationId,
  onRetryAccepted,
}) => {
  const { t } = useTranslation('admin');
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');

  const handleRetry = async () => {
    if (!token) {
      return;
    }

    try {
      setLoading(true);
      await retryPublication(
        publicationId,
        { reason: t('publications.detail.retry.reason'), force: false },
        token
      );

      setSnackbarSeverity('success');
      setSnackbarMessage(t('publications.detail.retry.retryAccepted'));
      onRetryAccepted?.();
    } catch (error: any) {
      const statusCode = error?.response?.status;

      if (statusCode === 409) {
        setSnackbarSeverity('warning');
        setSnackbarMessage(t('publications.detail.retry.alreadyProcessing'));
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(t('publications.detail.retry.retryFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
        onClick={handleRetry}
        disabled={loading}
      >
        {loading ? t('publications.detail.retry.retrying') : t('publications.detail.retry.action')}
      </Button>

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarMessage(null)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RetryPublicationButton;
